import { useState, useEffect, axios, Button, Input, Card, CardContent, CardHeader, CardTitle, Badge, Label, Loader2, Check, Save, Edit, Key, Lock, Trash2, AlertTriangle, Copy, Upload, Palette, Bell, BACKEND_URL, API, useAuth, toast } from "@/pages/shared";
import { useNavigate, Link } from "react-router-dom";
import { Monitor, LogOut, Shield } from "lucide-react";
import { Sparkles } from "lucide-react";
import { LinkedInPersonalSection } from "../../components/settings/LinkedInPersonalSection";
import { TwitterPersonalSection } from "../../components/settings/TwitterPersonalSection";
import { BlueskyPersonalSection } from "../../components/settings/BlueskyPersonalSection";
import { TelegramPersonalSection } from "../../components/settings/TelegramPersonalSection";
import { ConnectedPlatformsSection } from "../../components/settings/ConnectedPlatformsSection";
import { BusinessBrainSection } from "../../components/settings/BusinessBrainSection";
import { CLAIMS } from "@/config/claims";

const TONES = ["professional", "friendly", "bold", "playful", "luxurious", "technical", "casual"];
const VISUAL_STYLES = ["modern", "minimalist", "vintage", "bold", "hand-crafted", "photo-realistic", "illustrated"];

// ============== BRAND KIT SECTION ==============
// Central source of truth for the user's brand identity. Injected into every
// text + image generation prompt so output looks consistently on-brand —
// the same wedge Taka.ai uses, but combined with our auto-publishing stack.
const BrandKitSection = () => {
  const { token } = useAuth();
  const [bk, setBk] = useState({
    business_name: "", tagline: "", primary_color: "#7C5CFC", secondary_color: "#0f172a",
    logo_url: "", tone: "friendly", audience: "", say_dos: [], say_donts: [],
    visual_style: "modern",
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newDo, setNewDo] = useState("");
  const [newDont, setNewDont] = useState("");

  useEffect(() => {
    axios.get(`${API}/brand-kit`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBk((prev) => ({ ...prev, ...r.data })))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip logo_storage_path / updated_at — server-managed
      const { logo_storage_path, updated_at, ...payload } = bk;  // eslint-disable-line no-unused-vars
      const r = await axios.put(`${API}/brand-kit`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setBk((prev) => ({ ...prev, ...r.data.brand_kit }));
      toast.success("Brand Kit saved — applies to all future generations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/brand-kit/logo`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setBk((prev) => ({ ...prev, logo_url: r.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploadingLogo(false);
  };

  const addDo = () => { if (newDo.trim()) { setBk({ ...bk, say_dos: [...bk.say_dos, newDo.trim()] }); setNewDo(""); } };
  const addDont = () => { if (newDont.trim()) { setBk({ ...bk, say_donts: [...bk.say_donts, newDont.trim()] }); setNewDont(""); } };
  const removeDo = (i) => setBk({ ...bk, say_dos: bk.say_dos.filter((_, idx) => idx !== i) });
  const removeDont = (i) => setBk({ ...bk, say_donts: bk.say_donts.filter((_, idx) => idx !== i) });

  const completion = (() => {
    const fields = [bk.business_name, bk.tagline, bk.audience, bk.logo_url, bk.say_dos.length > 0, bk.say_donts.length > 0];
    return Math.round((fields.filter(Boolean).length / fields.length) * 100);
  })();

  return (
    <Card className="glass border-violet-500/20" data-testid="settings-brand-kit-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-violet-500" /> Brand Kit
          <Badge className="bg-violet-500/15 text-violet-400 border border-violet-500/30 ml-2">Core</Badge>
          <span className="ml-auto text-xs text-muted-foreground" data-testid="brand-kit-completion">{completion}% complete</span>
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Set this once. Every post, image, and email Creatara generates pulls from it — colours, tone, audience, even what NOT to say.
        </p>
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {/* Identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Business / brand name</Label>
                <Input value={bk.business_name} onChange={(e) => setBk({ ...bk, business_name: e.target.value })} placeholder="e.g. Atiba AI Consulting" className="bg-white/5 border-white/10" data-testid="bk-business-name" />
              </div>
              <div>
                <Label className="text-xs">Tagline (one line)</Label>
                <Input value={bk.tagline} onChange={(e) => setBk({ ...bk, tagline: e.target.value })} placeholder="e.g. AI that pays for itself" className="bg-white/5 border-white/10" data-testid="bk-tagline" />
              </div>
            </div>

            {/* Visual identity */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Primary colour</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={bk.primary_color} onChange={(e) => setBk({ ...bk, primary_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer bg-transparent" data-testid="bk-primary-color" />
                  <Input value={bk.primary_color} onChange={(e) => setBk({ ...bk, primary_color: e.target.value })} className="bg-white/5 border-white/10 flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Secondary colour</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={bk.secondary_color} onChange={(e) => setBk({ ...bk, secondary_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer bg-transparent" data-testid="bk-secondary-color" />
                  <Input value={bk.secondary_color} onChange={(e) => setBk({ ...bk, secondary_color: e.target.value })} className="bg-white/5 border-white/10 flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Logo</Label>
                <div className="flex items-center gap-3">
                  {bk.logo_url ? (
                    <img src={`${BACKEND_URL}${bk.logo_url}`} alt="logo" className="h-9 w-9 object-contain bg-white/5 rounded" data-testid="bk-logo-preview" />
                  ) : (
                    <div className="h-9 w-9 bg-white/5 rounded flex items-center justify-center"><Upload className="w-4 h-4 text-muted-foreground" /></div>
                  )}
                  <label className="cursor-pointer flex-1">
                    <input type="file" accept="image/*" onChange={handleLogo} className="hidden" data-testid="bk-logo-input" />
                    <span className="inline-block px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 rounded border border-white/10 transition">{uploadingLogo ? "Uploading..." : bk.logo_url ? "Replace" : "Upload"}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Tone + visual style */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tone</Label>
                <select value={bk.tone} onChange={(e) => setBk({ ...bk, tone: e.target.value })} className="w-full h-9 px-3 rounded bg-white/5 border border-white/10 text-sm" data-testid="bk-tone">
                  {TONES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Visual style (for AI images)</Label>
                <select value={bk.visual_style} onChange={(e) => setBk({ ...bk, visual_style: e.target.value })} className="w-full h-9 px-3 rounded bg-white/5 border border-white/10 text-sm" data-testid="bk-visual-style">
                  {VISUAL_STYLES.map((t) => <option key={t} value={t} className="bg-card">{t}</option>)}
                </select>
              </div>
            </div>

            {/* Audience */}
            <div>
              <Label className="text-xs">Target audience</Label>
              <Input value={bk.audience} onChange={(e) => setBk({ ...bk, audience: e.target.value })} placeholder="e.g. Solopreneurs earning from content + affiliates, ages 25-45, mostly UK / US" className="bg-white/5 border-white/10" data-testid="bk-audience" />
            </div>

            {/* Do / Don't lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-teal-500/5 border border-teal-500/20">
                <Label className="text-xs text-teal-400 font-semibold">✓ Always include / emphasise</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={newDo} onChange={(e) => setNewDo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDo()} placeholder="e.g. Lead with revenue impact" className="bg-white/5 border-white/10 text-sm h-8" data-testid="bk-new-do" />
                  <Button size="sm" onClick={addDo} className="h-8 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/30" data-testid="bk-add-do">+</Button>
                </div>
                <div className="space-y-1 mt-2">
                  {bk.say_dos.map((d, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs p-1.5 rounded bg-white/5" data-testid={`bk-do-${i}`}>
                      <span>{d}</span>
                      <button onClick={() => removeDo(i)} className="text-muted-foreground hover:text-violet-400">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
                <Label className="text-xs text-violet-400 font-semibold">✗ Never say / avoid</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={newDont} onChange={(e) => setNewDont(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDont()} placeholder="e.g. Generic agency speak" className="bg-white/5 border-white/10 text-sm h-8" data-testid="bk-new-dont" />
                  <Button size="sm" onClick={addDont} className="h-8 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 border border-violet-500/30" data-testid="bk-add-dont">+</Button>
                </div>
                <div className="space-y-1 mt-2">
                  {bk.say_donts.map((d, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs p-1.5 rounded bg-white/5" data-testid={`bk-dont-${i}`}>
                      <span>{d}</span>
                      <button onClick={() => removeDont(i)} className="text-muted-foreground hover:text-violet-400">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 rounded-full w-full sm:w-auto" data-testid="bk-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save Brand Kit</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



// ============== BRAND VOICE SECTION ==============
const BrandVoiceSection = () => {
  // Initialize with empty defaults so the card renders immediately even if the API is slow / errors.
  const [bv, setBv] = useState({ description: "", forbidden_words: "", sample_content: "", configured: false });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/brand-voice`)
      .then((r) => setBv(r.data))
      .catch(() => { /* keep defaults */ })
      .finally(() => setLoaded(true));
  }, []);

  const update = (k, v) => setBv((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/account/brand-voice`, {
        description: bv.description, forbidden_words: bv.forbidden_words, sample_content: bv.sample_content,
      });
      toast.success("Brand voice saved — applied to every future generation");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <Card className="glass" data-testid="settings-brandvoice-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Brand Voice {bv.configured && <Badge className="bg-teal-600 text-xs">Active</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">Train the AI to write in your distinct voice — applied automatically to every generation.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Voice description</Label>
          <Input value={bv.description || ""} onChange={(e) => update("description", e.target.value)} placeholder="e.g. Friendly, witty, no jargon. Like a coach talking to a peer." className="mt-1 bg-white/5 border-white/10" data-testid="settings-bv-description" maxLength={500} disabled={!loaded} />
        </div>
        <div>
          <Label>Words/phrases to avoid (comma-separated)</Label>
          <Input value={bv.forbidden_words || ""} onChange={(e) => update("forbidden_words", e.target.value)} placeholder="e.g. utilize, leverage, synergy, game-changer" className="mt-1 bg-white/5 border-white/10" data-testid="settings-bv-forbidden" maxLength={500} disabled={!loaded} />
        </div>
        <div>
          <Label>Sample of your previous writing (50-3000 chars)</Label>
          <textarea
            value={bv.sample_content || ""}
            onChange={(e) => update("sample_content", e.target.value)}
            placeholder="Paste 1-2 of your best previous social posts or paragraphs from your blog. The AI uses this as a style reference."
            className="mt-1 w-full min-h-[120px] bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm"
            data-testid="settings-bv-sample"
            maxLength={3000}
            disabled={!loaded}
          />
          <p className="text-xs text-muted-foreground mt-1">{(bv.sample_content || "").length} / 3000</p>
        </div>
        <Button onClick={save} disabled={saving || !loaded} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="settings-bv-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Brand Voice
        </Button>
      </CardContent>
    </Card>
  );
};

// ============== EMBED BADGES SECTION ==============
const EmbedBadgesSection = ({ user }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.referral_code) return;
    axios.get(`${API}/badges/${user.referral_code}/snippets`)
      .then((r) => setData(r.data))
      .catch(() => { /* surface nothing — section degrades gracefully */ })
      .finally(() => setLoaded(true));
  }, [user?.referral_code]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} snippet copied`);
  };

  return (
    <Card className="glass" data-testid="settings-badges-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" /> Embed badges
          <Badge className="bg-teal-500/15 text-teal-400 border border-teal-500/30 ml-2">Earn credits</Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Drop one of these on your site, blog, README, or Notion. Every click attributed to you — every signup gives you {CLAIMS.referral.creditsPerSignup} free credits.
        </p>
      </CardHeader>
      <CardContent>
        {!loaded ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Couldn't load badges. Try refreshing.</p>
        ) : (
          <div className="space-y-4">
            {data.variants.map((v) => {
              // Use REACT_APP_BACKEND_URL for the LIVE preview so dev/preview env renders correctly.
              // The copied snippet (html/markdown/bbcode) still uses PUBLIC_APP_URL from the backend,
              // which is what users actually embed on their own sites.
              const livePreviewUrl = `${BACKEND_URL}/api/badges/${user.referral_code}.svg?variant=${v.key}`;
              return (
              <div key={v.key} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`badge-variant-${v.key}`}>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <img src={livePreviewUrl} alt={`${v.label} ${v.value}`} height="28" className="h-7" data-testid={`badge-preview-${v.key}`} />
                    <span className="text-xs text-muted-foreground">Variant: <span className="font-mono">{v.key}</span></span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => copy(v.html, "HTML")} data-testid={`badge-copy-html-${v.key}`}>
                      <Copy className="w-3 h-3 mr-1" />HTML
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => copy(v.markdown, "Markdown")} data-testid={`badge-copy-md-${v.key}`}>
                      <Copy className="w-3 h-3 mr-1" />Markdown
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => copy(v.bbcode, "BBCode")} data-testid={`badge-copy-bb-${v.key}`}>
                      <Copy className="w-3 h-3 mr-1" />BBCode
                    </Button>
                  </div>
                </div>
                <pre className="text-[10px] text-muted-foreground bg-black/30 p-2 rounded overflow-x-auto"><code>{v.html}</code></pre>
              </div>
              );
            })}
            <p className="text-xs text-muted-foreground pt-2">
              💡 Pro tip: stick the "Built with" variant in your blog footer + the "Earning with" variant in your Twitter pinned thread. Each badge is auto-tagged with <span className="font-mono">utm_source=badge</span> so you'll see exactly which one drives signups in your <Link to="/dashboard/referrals" className="text-violet-400 underline">Referrals dashboard</Link>.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ============== NOTIFICATIONS SECTION ==============
const NotificationsSection = () => {
  const DEFAULTS = { new_lead: true, referral_signup: true, payment_receipt: true, weekly_summary: false, product_updates: true, new_device_alert: true, onboarding_drip: true };
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/notifications`)
      .then((r) => setPrefs({ ...DEFAULTS, ...r.data }))
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key) => {
    if (!loaded) return;
    const prev = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await axios.put(`${API}/account/notifications`, { [key]: next[key] });
    } catch (e) {
      toast.error("Failed to save");
      setPrefs(prev);
    }
    setSaving(false);
  };

  const items = [
    { key: "new_lead", title: "New lead captured", desc: "When someone submits one of your lead capture forms." },
    { key: "referral_signup", title: "New referral signup", desc: "When someone signs up via your referral link — includes credit bonus." },
    { key: "new_device_alert", title: "New device sign-in alerts", desc: "Email when someone signs in from a device we haven't seen on your account — with a one-click 'That wasn't me' link." },
    { key: "payment_receipt", title: "Payment receipts", desc: "Confirmation emails after successful subscription payments." },
    { key: "weekly_summary", title: "Weekly summary", desc: "A Monday-morning digest of last week's content, leads, and referrals." },
    { key: "product_updates", title: "Product updates", desc: "New features, releases, and tips to get more out of Creatara." },
    { key: "onboarding_drip", title: "Onboarding tips", desc: "A few helpful nudges in your first 2 weeks — first post, monetisation, plan upgrade. Auto-stops after Day 14." },
  ];

  return (
    <Card className="glass" data-testid="settings-notifications-card">
      <CardHeader><CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> Email Notifications</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {items.map((it) => (
          <label key={it.key} className="flex items-start gap-3 cursor-pointer select-none py-1">
            <input
              type="checkbox"
              checked={!!prefs[it.key]}
              onChange={() => toggle(it.key)}
              disabled={saving}
              className="accent-violet-500 mt-1"
              data-testid={`settings-notif-${it.key}`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{it.title}</p>
              <p className="text-xs text-muted-foreground">{it.desc}</p>
            </div>
          </label>
        ))}
      </CardContent>
    </Card>
  );
};

// ============== PROFILE SECTION ==============
const ProfileSection = ({ user, updateUser }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [disclosure, setDisclosure] = useState("");
  const [savingDisc, setSavingDisc] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user?.name]);
  useEffect(() => {
    axios.get(`${API}/account/profile`).then((r) => setDisclosure(r.data.disclosure_line || "")).catch(() => {});
  }, []);

  const saveDisclosure = async () => {
    setSavingDisc(true);
    try {
      await axios.put(`${API}/account/profile`, { disclosure_line: disclosure });
      toast.success("Disclosure line saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSavingDisc(false);
  };

  const save = async () => {
    if (!name.trim() || name.trim().length < 2) { toast.error("Name must be at least 2 characters"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/account/profile`, { name: name.trim() });
      updateUser({ ...user, name: name.trim() });
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setSaving(false);
  };

  return (
    <Card className="glass" data-testid="settings-profile-card">
      <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl text-violet-500 font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="settings-name-input" />
                <Button size="sm" onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="settings-save-name-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setName(user?.name || ""); }} data-testid="settings-cancel-name-btn">Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-xl font-semibold truncate">{user?.name}</p>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="settings-edit-name-btn"><Edit className="w-4 h-4" /></Button>
              </div>
            )}
            <p className="text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <div className="pt-3 border-t border-white/10">
          <Label className="text-sm">Affiliate disclosure line</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">One-click inserted whenever a post contains a tracked link. You're responsible for your own disclosures — see the <a href="/trust" className="underline text-violet-400">Trust page</a>.</p>
          <div className="flex items-center gap-2">
            <Input value={disclosure} onChange={(e) => setDisclosure(e.target.value)} maxLength={300} placeholder={CLAIMS.defaultDisclosure} className="bg-white/5 border-white/10 flex-1" data-testid="settings-disclosure-input" />
            <Button size="sm" onClick={saveDisclosure} disabled={savingDisc} className="bg-violet-600 hover:bg-violet-700" data-testid="settings-save-disclosure-btn">{savingDisc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== USAGE SECTION ==============
const UsageSection = () => {
  const [usage, setUsage] = useState({ plan: "free", used: 0, limit: 10, referral_credits: 0, effective_limit: 10, unlimited: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/usage`)
      .then((r) => setUsage(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const pct = usage.unlimited ? 100 : Math.min(100, Math.round((usage.used / Math.max(1, usage.effective_limit)) * 100));
  const planLabel = usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1);

  return (
    <Card className="glass" data-testid="settings-usage-card">
      <CardHeader><CardTitle>Usage This Period</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-3xl font-bold">{loaded ? usage.used : "—"}<span className="text-muted-foreground text-base font-normal"> / {usage.unlimited ? "∞" : usage.effective_limit} generations</span></p>
          <Badge variant="secondary" className="text-xs">{planLabel} plan</Badge>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-600 to-violet-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {usage.referral_credits > 0 && (
          <p className="text-sm text-teal-500">+{usage.referral_credits} bonus generations from referrals</p>
        )}
        {!usage.unlimited && pct >= 80 && (
          <p className="text-sm text-amber-500">You've used {pct}% of your monthly quota. Consider upgrading.</p>
        )}
      </CardContent>
    </Card>
  );
};

// ============== SUBSCRIPTION (manage) ==============
const SubscriptionManageSection = ({ user }) => {
  const [opening, setOpening] = useState(false);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await axios.post(`${API}/account/billing-portal`);
      // If backend returns a human-readable message, that's the graceful
      // fallback (no Stripe customer yet / portal unavailable). Show it and
      // stay in place instead of redirecting to a meaningless URL.
      if (res.data.message) {
        toast.info(res.data.message);
      } else if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (e) { toast.error("Failed to open billing portal"); }
    setOpening(false);
  };

  return (
    <Card className="glass" data-testid="settings-subscription-card">
      <CardHeader><CardTitle>Subscription</CardTitle></CardHeader>
      <CardContent className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-lg font-semibold">{(user?.plan || "free").toUpperCase()} Plan</p>
          <p className="text-sm text-muted-foreground">To change plans, scroll to Subscription Plans below.</p>
        </div>
        <Button variant="outline" onClick={openPortal} disabled={opening} data-testid="settings-billing-portal-btn">
          {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage Billing"}
        </Button>
      </CardContent>
    </Card>
  );
};

// ============== API KEYS SECTION (Enterprise gated) ==============
const ApiKeysSection = ({ user }) => {
  const isEnterprise = user?.plan === "enterprise";
  const [keys, setKeys] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [revealedKey, setRevealedKey] = useState(null);

  const fetchKeys = async () => {
    try { const res = await axios.get(`${API}/api-keys`); setKeys(res.data); } catch (e) {}
  };

  useEffect(() => { if (isEnterprise) fetchKeys(); }, [isEnterprise]);

  const create = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { name: name || "My API Key" });
      setRevealedKey(res.data);
      setName("");
      fetchKeys();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create key"); }
    setCreating(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Any apps using it will lose access immediately.")) return;
    try { await axios.delete(`${API}/api-keys/${id}`); toast.success("Key revoked"); fetchKeys(); }
    catch (e) { toast.error("Failed to revoke"); }
  };

  const copyKey = (val) => { navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); };

  if (!isEnterprise) {
    return (
      <Card className="glass border-white/10" data-testid="settings-apikeys-locked">
        <CardHeader><CardTitle className="flex items-center gap-2"><Key className="w-5 h-5" /> API Keys <Badge className="bg-amber-600 ml-2">Enterprise</Badge></CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">Programmatic access to Creatara — generate content from your own apps, scripts, or automations. Available on the Enterprise plan.</p>
          <p className="text-xs text-muted-foreground">Upgrade below to unlock.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass" data-testid="settings-apikeys-card">
      <CardHeader><CardTitle className="flex items-center gap-2"><Key className="w-5 h-5" /> API Keys</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {revealedKey && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2" data-testid="settings-revealed-key">
            <p className="text-sm font-medium text-amber-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Save this key now — you won't see it again</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs p-2 rounded bg-black/40 break-all">{revealedKey.key}</code>
              <Button size="sm" variant="outline" onClick={() => copyKey(revealedKey.key)} data-testid="settings-copy-key-btn"><Copy className="w-4 h-4" /></Button>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setRevealedKey(null)} data-testid="settings-dismiss-key-btn">I've saved it</Button>
          </div>
        )}

        <div className="flex gap-2">
          <Input placeholder="Key name (e.g. 'Zapier integration')" value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="settings-newkey-name-input" />
          <Button onClick={create} disabled={creating} className="bg-violet-600 hover:bg-violet-700" data-testid="settings-create-key-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Key"}
          </Button>
        </div>

        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No API keys yet</p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{k.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{k.key_prefix}…</p>
                  <p className="text-xs text-muted-foreground">Last used: {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => revoke(k.id)} data-testid={`settings-revoke-key-${k.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        )}

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-white transition-colors">How to use your API key</summary>
          <pre className="mt-2 p-3 rounded bg-black/40 overflow-x-auto whitespace-pre-wrap break-all">{`curl -X POST ${BACKEND_URL}/api/content/generate \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"topic":"morning routines","content_type":"blog","tone":"professional","length":"medium"}'`}</pre>
        </details>
      </CardContent>
    </Card>
  );
};

// ============== WHITE-LABEL SECTION (Enterprise gated) ==============
const WhiteLabelSection = ({ user }) => {
  const isEnterprise = user?.plan === "enterprise";
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/branding`).then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const update = (patch) => setBranding((b) => ({ ...b, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, {
        brand_name: branding.brand_name,
        primary_color: branding.primary_color,
        custom_signature: branding.custom_signature,
        powered_by_enabled: branding.powered_by_enabled,
      });
      toast.success("Branding saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/branding/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ logo_url: res.data.logo_url });
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  if (!isEnterprise) {
    return (
      <Card className="glass" data-testid="settings-whitelabel-locked">
        <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> White-Label <Badge className="bg-amber-600 ml-2">Enterprise</Badge></CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Replace Creatara branding with your own — custom logo, brand color, and signatures on every share & email. Perfect for agencies and resellers.</p>
        </CardContent>
      </Card>
    );
  }

  if (!branding) return null;

  return (
    <Card className="glass" data-testid="settings-whitelabel-card">
      <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> White-Label</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground -mt-2 mb-2 p-3 rounded bg-blue-500/10 border border-blue-500/30">
          <strong>This is your personal account branding.</strong> It applies to your dashboard chrome and share captions when you're <em>not</em> in a client workspace. To brand a specific client (logo, colour, name shown when switched into that workspace), go to <Link to="/dashboard/workspaces" className="underline text-blue-400">Workspaces → Edit</Link>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Brand Name</Label>
            <Input value={branding.brand_name || ""} onChange={(e) => update({ brand_name: e.target.value })} className="mt-1 bg-white/5 border-white/10" data-testid="settings-brand-name-input" />
          </div>
          <div>
            <Label>Primary Color (hex)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={branding.primary_color || "#7C5CFC"} onChange={(e) => update({ primary_color: e.target.value })} className="h-10 w-14 rounded border border-white/10 bg-transparent cursor-pointer" data-testid="settings-color-picker" />
              <Input value={branding.primary_color || ""} onChange={(e) => update({ primary_color: e.target.value })} placeholder="#7C5CFC" className="bg-white/5 border-white/10 flex-1 font-mono" data-testid="settings-color-hex-input" />
            </div>
          </div>
        </div>

        <div>
          <Label>Logo</Label>
          <div className="flex items-center gap-4 mt-1">
            {branding.logo_url ? (
              <img src={branding.logo_url.startsWith("http") ? branding.logo_url : `${BACKEND_URL}${branding.logo_url}`} alt="Brand logo" className="w-16 h-16 rounded-lg object-cover bg-white/5" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center text-xs text-muted-foreground">No logo</div>
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="settings-logo-upload-input" />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-sm">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Upload logo"}
              </span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-1">PNG/JPG, max 5MB. Square images work best.</p>
        </div>

        <div>
          <Label>Custom Share Signature</Label>
          <Input value={branding.custom_signature || ""} onChange={(e) => update({ custom_signature: e.target.value })} placeholder="Generated with My Brand" className="mt-1 bg-white/5 border-white/10" data-testid="settings-signature-input" />
          <p className="text-xs text-muted-foreground mt-1">Appended to share captions. Leave empty to omit.</p>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={!!branding.powered_by_enabled} onChange={(e) => update({ powered_by_enabled: e.target.checked })} className="accent-violet-500" data-testid="settings-powered-by-toggle" />
          Show "Powered by Creatara" footer in shared content
        </label>

        <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="settings-save-branding-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Branding
        </Button>
      </CardContent>
    </Card>
  );
};

// ============== SUBSCRIPTION TIERS (kept at bottom) ==============
const SubscriptionTiersSection = ({ user, token }) => {
  const [upgrading, setUpgrading] = useState(false);

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { plan, origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error("Failed"); setUpgrading(false); }
  };

  // Tier rank — used to decide whether a button is an Upgrade (higher tier) or a downgrade (lower tier).
  const RANK = { free: 0, pro: 1, enterprise: 2 };
  const currentRank = RANK[user?.plan] ?? 0;

  const plans = CLAIMS.plans.map((p) => ({
    name: p.name, price: p.price, features: p.entitlements.slice(0, 3), plan: p.id,
  })).map((t) => ({ ...t, current: user?.plan === t.plan, isUpgrade: RANK[t.plan] > currentRank, isLower: RANK[t.plan] < currentRank }));

  return (
    <Card className="glass" data-testid="settings-pricing-card">
      <CardHeader><CardTitle>Subscription Plans</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((tier) => (
            <div key={tier.plan} className={`p-6 rounded-xl bg-white/5 ${tier.current ? 'border-2 border-violet-500' : ''} ${tier.isLower ? 'opacity-60' : ''}`} data-testid={`settings-plan-${tier.plan}`}>
              {tier.current && <Badge className="mb-2 bg-violet-600">Current</Badge>}
              {tier.isLower && !tier.current && <Badge variant="secondary" className="mb-2 text-[10px]" data-testid={`settings-plan-${tier.plan}-lower-badge`}>Lower tier</Badge>}
              <h3 className="text-xl font-bold">{tier.name}</h3>
              <p className="text-3xl font-bold my-2">{tier.price}<span className="text-sm text-muted-foreground">/mo</span></p>
              <ul className="space-y-2 my-4">{tier.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-teal-500" />{f}</li>)}</ul>
              {tier.isUpgrade && (
                <Button onClick={() => handleUpgrade(tier.plan)} disabled={upgrading} className="w-full btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full" data-testid={`settings-upgrade-${tier.plan}-btn`}>
                  {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upgrade"}
                </Button>
              )}
              {tier.isLower && !tier.current && (
                <p className="text-xs text-muted-foreground text-center" data-testid={`settings-plan-${tier.plan}-downgrade-hint`}>
                  To downgrade, use <span className="text-violet-400">Manage billing</span> above.
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== DANGER ZONE ==============
const DangerZoneSection = ({ logout }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const deleteAccount = async () => {
    const confirm1 = window.prompt("This will permanently delete your account and all your data (content, templates, leads, affiliate links). Type DELETE to confirm.");
    if (confirm1 !== "DELETE") return;
    setBusy(true);
    try {
      await axios.delete(`${API}/account`);
      toast.success("Account deleted");
      await logout();
      navigate("/");
    } catch (e) { toast.error("Failed to delete account"); setBusy(false); }
  };

  return (
    <Card className="glass border-violet-600/30" data-testid="settings-danger-card">
      <CardHeader><CardTitle className="text-violet-500 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Danger Zone</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">Permanently delete your account and all associated data. This cannot be undone.</p>
        <Button variant="destructive" onClick={deleteAccount} disabled={busy} className="rounded-full" data-testid="settings-delete-account-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Delete my account
        </Button>
      </CardContent>
    </Card>
  );
};

// ============== ACTIVE SESSIONS (multi-device) ==============
const ActiveSessionsSection = () => {
  const [data, setData] = useState({ sessions: [], limit: 1, plan: "free" });
  const [loading, setLoading] = useState(true);
  const [busyAll, setBusyAll] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/account/sessions`);
      setData(r.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const killOne = async (jti) => {
    try {
      await axios.delete(`${API}/account/sessions/${jti}`);
      toast.success("Device signed out");
      refresh();
    } catch (e) { toast.error("Failed to sign out device"); }
  };

  const killOthers = async () => {
    if (!window.confirm("Sign out every other device? You'll stay signed in here.")) return;
    setBusyAll(true);
    try {
      const r = await axios.delete(`${API}/account/sessions`);
      toast.success(`Signed out ${r.data.count} device${r.data.count === 1 ? "" : "s"}`);
      refresh();
    } catch (e) { toast.error("Failed to sign out others"); }
    setBusyAll(false);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <Card className="glass" data-testid="settings-sessions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-teal-500" /> Active Sessions</CardTitle>
        <div className="text-sm text-muted-foreground mt-1">Every device signed into your account. Your <Badge variant="secondary" className="text-xs mx-1">{data.plan?.toUpperCase()}</Badge> plan allows <strong>{data.limit}</strong> concurrent device{data.limit === 1 ? "" : "s"} — logging in on a new one automatically signs out the oldest.</div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : data.sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active sessions.</p>
        ) : (
          data.sessions.map((s) => (
            <div key={s.jti} className={`flex items-center justify-between gap-3 p-3 rounded-lg ${s.is_current ? "bg-teal-600/10 border border-teal-600/30" : "bg-white/5"}`} data-testid={`session-${s.jti}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Monitor className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{s.device}</p>
                    {s.is_current && <Badge className="bg-teal-600 text-white text-[10px]">This device</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {s.ip ? `${s.ip} · ` : ""}Last active {fmt(s.last_used_at)}
                  </p>
                </div>
              </div>
              {!s.is_current && (
                <Button variant="ghost" size="sm" onClick={() => killOne(s.jti)} data-testid={`session-revoke-${s.jti}`}>
                  <LogOut className="w-4 h-4 mr-1" /> Sign out
                </Button>
              )}
            </div>
          ))
        )}
        {data.sessions.length > 1 && (
          <Button variant="outline" onClick={killOthers} disabled={busyAll} className="mt-3" data-testid="sessions-revoke-others-btn">
            {busyAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
            Sign out all other devices
          </Button>
        )}
        <p className="text-xs text-muted-foreground pt-1">If you don't recognise a device, sign it out immediately and change your password.</p>
      </CardContent>
    </Card>
  );
};

// ============== MAIN PAGE ==============
export default function SettingsPage() {
  const { user, token, updateUser, logout } = useAuth();

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold mb-2">Settings</h1><p className="text-muted-foreground">Manage your account, subscription, and integrations.</p></div>

      <ProfileSection user={user} updateUser={updateUser} />
      <BusinessBrainSection />
      <ActiveSessionsSection />
      <BrandKitSection />
      <BrandVoiceSection />
      <UsageSection />
      <SubscriptionManageSection user={user} />
      <NotificationsSection />
      <ConnectedPlatformsSection user={user} />
      <LinkedInPersonalSection />
      <TwitterPersonalSection />
      <BlueskyPersonalSection />
      <TelegramPersonalSection />
      <EmbedBadgesSection user={user} />
      <ApiKeysSection user={user} />
      <WhiteLabelSection user={user} />
      <SubscriptionTiersSection user={user} token={token} />
      <DangerZoneSection logout={logout} />
    </div>
  );
}
